import React, { useEffect, useRef, useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { useQuestChain } from '../hooks/useQuestChain';
import { useRideGate } from '../hooks/useRideGate';
import { camBasis } from '../services/worldObjects';
import { TOWN_LOCATIONS } from '../rooms';
import { IS_GUEST } from '../config';
import { api } from '../services/api';
import { audioEngine } from '../services/audioEngine';

// SPOKEN NUDGES (Khari, 2026-08-03: "this demo should be voice rich"): the
// four lines live server-side (WALK_NUDGES) with cached clips in Lyra's call
// voice; text + audio arrive together so they can never drift apart. Fetched
// once per page, keyed `${reason}_${destination}`.
let walkLinesCache = null;
function fetchWalkLines() {
  if (walkLinesCache) return Promise.resolve(walkLinesCache);
  return api.walkLines()
    .then((d) => {
      walkLinesCache = Object.fromEntries((d?.lines || []).map((l) => [l.key, l]));
      return walkLinesCache;
    })
    .catch(() => ({}));
}

// WalkGuide — the walk's required guardrail (Build_Game_Intro §4.4).
//
// "Getting lost here is the single most likely churn point in the entire
// intro." Two answers, both quiet:
//
//   THE WAYPOINT — while the session is what's next and you're on the street,
//   a small chip names the studio and roughly how far it is. Wayfinding UI,
//   presented as UI — same standing as the map markers.
//
//   THE NUDGE — if you stall (nothing moves for a while) or stray (the studio
//   keeps getting further away), Lyra texts: "you good? studio's on 4th."
//   Fired sparingly, twice ever per session — a manager checks in, she does
//   not nag. Wandering is ALLOWED and rewarded; the nudge exists for the
//   player who is lost, not the one who is exploring.
//
// Position comes from camBasis — the per-frame module bridge the placement
// tools already read — polled once a second, because a DOM component that
// re-renders on every camera frame is the bug the bridge exists to prevent.

const STALL_MS = 50000;      // no meaningful movement for this long → nudge
const STRAY_UNITS = 26;      // this much further from the studio than your best → nudge
const NUDGE_COOLDOWN_MS = 90000;
const MAX_NUDGES = 2;

export default function WalkGuide() {
  const entered = useWorldStore((s) => s.entered);
  const interior = useWorldStore((s) => s.interior);
  const scene = useWorldStore((s) => s.scene);
  const ride = useWorldStore((s) => s.ride);
  const myAvatarId = useWorldStore((s) => s.myAvatarId);
  const { statusOf, loaded } = useQuestChain(myAvatarId);

  const [dist, setDist] = useState(null);
  const [nudge, setNudge] = useState(null);   // the text on screen, briefly
  const track = useRef({ lastPos: null, lastMoveAt: 0, bestDist: Infinity, nudges: 0, lastNudgeAt: 0 });

  // A GUEST who just left Spor1's car walks to the SALON — that is where the
  // account (and everything after it) happens. Members walk to the studio.
  const guestProgress = useWorldStore((s) => s.guestProgress);
  const guestToSalon = IS_GUEST && !!guestProgress?.rideDone;
  const dest = TOWN_LOCATIONS.find((l) => l.key === (guestToSalon ? 'salon' : 'studio'));
  const sessionQuest = loaded ? statusOf('come_up_the_session') : null;
  // NOBODY NUDGES YOU BEFORE THE STORY STARTS (Khari, 2026-08-06: "Lyra sends me
  // a text message to remind me to go to the studio, though I haven't answered
  // the phone"). The session quest can already be OPEN at spawn for a seeded or
  // returning artist — M8 fills beats in from an existing profile — so quest
  // state alone says nothing about whether she has spoken to you yet. Same rule,
  // same hook, as the three ride surfaces.
  const { arrivalDone } = useRideGate();
  const walking = entered && !interior && !scene && !ride && !!dest && arrivalDone
    && (guestToSalon || (!!sessionQuest && !IS_GUEST));

  useEffect(() => {
    if (!walking) { setDist(null); return undefined; }
    const t = track.current;
    t.lastPos = null; t.lastMoveAt = Date.now(); t.bestDist = Infinity;

    const tick = () => {
      const now = Date.now();
      const px = camBasis.px, pz = camBasis.pz;
      const d = Math.hypot(dest.world[0] - px, dest.world[1] - pz);
      setDist(d);

      if (t.lastPos) {
        const moved = Math.hypot(px - t.lastPos[0], pz - t.lastPos[1]);
        if (moved > 1.5) t.lastMoveAt = now;
      }
      t.lastPos = [px, pz];
      t.bestDist = Math.min(t.bestDist, d);

      const may = t.nudges < MAX_NUDGES && now - t.lastNudgeAt > NUDGE_COOLDOWN_MS;
      if (may && now - t.lastMoveAt > STALL_MS) {
        t.nudges += 1; t.lastNudgeAt = now; t.lastMoveAt = now;
        speakNudge('stall', guestToSalon
          ? "you good? Sassy's is right there — go get yourself sorted."
          : "you good? studio's on 4th — short walk, they're waiting.");
      } else if (may && d - t.bestDist > STRAY_UNITS) {
        t.nudges += 1; t.lastNudgeAt = now; t.bestDist = d;
        speakNudge('stray', guestToSalon
          ? "wrong way — the salon's back the other way. Pink front, can't miss it."
          : "wrong way, superstar. Turn around — studio's back the other way.");
      }
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [walking]); // eslint-disable-line react-hooks/exhaustive-deps

  // A nudge shows its chip AND speaks, when the clip exists — server text
  // wins over the local fallback so what she says is what the chip reads.
  const nudgeAudioRef = useRef(null);
  function speakNudge(reason, fallbackText) {
    const key = `${reason}_${guestToSalon ? 'salon' : 'studio'}`;
    fetchWalkLines().then((lines) => {
      const l = lines?.[key];
      setNudge(l?.line || fallbackText);
      if (l?.audioUrl) {
        try {
          nudgeAudioRef.current?.pause();
          const a = new Audio(l.audioUrl);
          nudgeAudioRef.current = a;
          audioEngine.duck(true);
          a.onended = () => audioEngine.duck(false);
          a.play().catch(() => audioEngine.duck(false));
        } catch { audioEngine.duck(false); }
      }
    });
  }
  useEffect(() => () => {
    nudgeAudioRef.current?.pause();
    audioEngine.duck(false);
  }, []);

  useEffect(() => {
    if (!nudge) return undefined;
    const t = setTimeout(() => setNudge(null), 7000);
    return () => clearTimeout(t);
  }, [nudge]);

  if (!walking) return null;

  const howFar = dist == null ? '' : dist < 14 ? 'right there' : dist < 42 ? 'up the block' : 'a few blocks';

  return (
    <>
      <div style={chip}>
        <span style={{ opacity: 0.65, marginRight: 6 }}>▸</span>
        {guestToSalon ? '💇 The Salon' : '🎙 The Studio'}{howFar ? ` · ${howFar}` : ''}
      </div>
      {nudge && (
        <div style={text}>
          <span style={{
            width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
            background: 'linear-gradient(160deg,#ffb25e,#ff5e78)', color: '#1a0f00',
            display: 'grid', placeItems: 'center', font: '800 12px system-ui',
          }}>L</span>
          <span style={{ font: '600 12.5px system-ui', lineHeight: 1.4 }}>{nudge}</span>
        </div>
      )}
    </>
  );
}

const chip = {
  position: 'fixed', top: 'max(14px, env(safe-area-inset-top))', right: 14, zIndex: 69,
  pointerEvents: 'none', padding: '7px 13px', borderRadius: 999,
  font: '700 12px system-ui', color: '#ffe9c4',
  border: '1px solid rgba(255,178,94,0.4)', background: 'rgba(20,12,8,0.78)',
  backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
};
const text = {
  position: 'fixed', left: '50%', transform: 'translateX(-50%)',
  top: 'max(60px, calc(env(safe-area-inset-top) + 46px))', zIndex: 71,
  display: 'flex', alignItems: 'center', gap: 9, maxWidth: 'min(340px, calc(100vw - 40px))',
  padding: '10px 14px', borderRadius: 14, color: '#ffe9c4', pointerEvents: 'none',
  border: '1px solid rgba(255,178,94,0.45)', background: 'rgba(20,12,8,0.92)',
  backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
  boxShadow: '0 14px 40px rgba(0,0,0,0.5)',
};
